// WordPress API Helper with WPML and ACF Support
const WORDPRESS_BASE_URL = "https://cms.qrhub.io/wp-json/wp/v2";

// Language mapping
const LANGUAGE_MAP = {
  en: "en",
  ar: "ar",
};

// Get current language from router or default to English
export const getCurrentLanguage = (router?: any): string => {
  if (router?.query?.lang) {
    return router.query.lang as string;
  }
  return "en";
};

// Build WordPress API URL with language support
export const buildWordPressUrl = (
  endpoint: string,
  params: Record<string, any> = {},
  language?: string
): string => {
  const baseUrl = `${WORDPRESS_BASE_URL}/${endpoint}`;
  const urlParams = new URLSearchParams();

  // Add language parameter for WPML (English is default, no lang parameter needed)
  if (
    language &&
    language !== "en" &&
    LANGUAGE_MAP[language as keyof typeof LANGUAGE_MAP]
  ) {
    urlParams.append(
      "lang",
      LANGUAGE_MAP[language as keyof typeof LANGUAGE_MAP]
    );
  }

  // Add other parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, value.toString());
    }
  });

  const queryString = urlParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// Custom error to signal not-found resources
export class NotFoundError extends Error {
  constructor(message: string = "Resource not found") {
    super(message);
    this.name = "NotFoundError";
  }
}

// Generic WordPress API fetch function
export const fetchWordPressData = async <T>(
  endpoint: string,
  params: Record<string, any> = {},
  language?: string
): Promise<T> => {
  const url = buildWordPressUrl(endpoint, params, language);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "omit",
      redirect: "follow",
    });

    if (response.status === 404) {
      throw new NotFoundError();
    }

    if (!response.ok) {
      throw new Error(
        `WordPress API error: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // Heuristic: For slug-based queries that return arrays, an empty array means not found
    if (
      Array.isArray(data) &&
      data.length === 0 &&
      Object.prototype.hasOwnProperty.call(params, "slug")
    ) {
      throw new NotFoundError();
    }

    return data;
  } catch (error) {
    console.error(`Error fetching WordPress data from ${url}:`, error);
    throw error;
  }
};

// Specific API functions for your content types
export const getProductsData = async (language?: string) => {
  return fetchWordPressData("products", { per_page: 100 }, language);
};

export const getSolutionsData = async (language?: string) => {
  return fetchWordPressData("solutions", { per_page: 100 }, language);
};

export const getArticlesData = async (language?: string) => {
  return fetchWordPressData("articles", { per_page: 100 }, language);
};

export const getArticlesCategories = async (language?: string) => {
  return fetchWordPressData("articles_categories", {}, language);
};

export const getArticlesCategoriesDataById = async (
  categoryId: number,
  language?: string,
  page?: number
) => {
  return fetchWordPressData(
    "articles_categories",
    {
      articles_categories: categoryId,
      per_page: 100,
      page: page,
    },
    language
  );
};

export const getHelpCenterData = async (language?: string) => {
  return fetchWordPressData("help_center", { per_page: 100 }, language);
};
export const getHelpCenterCategoriesData = async (
  language?: string,
  page?: number
) => {
  return fetchWordPressData(
    "help_center_categories",
    { per_page: 100, page: page },
    language
  );
};

export const getHelpCenterCategoryDataById = async (
  categoryId: number,
  language?: string,
  page?: number
) => {
  return fetchWordPressData(
    "help_center",
    {
      help_center_categories: categoryId,
      per_page: 100,
      page: page,
    },
    language
  );
};

// Get help center category information by ID (returns category details, not posts)
export const getHelpCenterCategoryById = async (
  categoryId: number,
  language?: string
) => {
  return fetchWordPressData(
    `help_center_categories/${categoryId}`,
    {},
    language
  );
};
export const getLegalPagesData = async (language?: string) => {
  return fetchWordPressData("legalPages", { per_page: 100 }, language);
};

export const getPlansData = async () => {
  return fetch("https://europe-west1-qrhub-live.cloudfunctions.net/fetchPlans");
};

// Get single post by ID with language support
export const getPostById = async (
  id: number,
  postType: string = "products",
  language?: string
) => {
  return fetchWordPressData(`${postType}/${id}`, {}, language);
};

// Get posts by category with language support
export const getPostsByCategory = async (
  categoryId: number,
  postType: string = "articles",
  language?: string,
  page?: number
) => {
  return fetchWordPressData(
    postType,
    {
      [`${postType}_categories`]: categoryId,
      per_page: 100,
      page: page,
    },
    language
  );
};

// Search posts with language support
export const searchPosts = async (
  searchTerm: string,
  postType: string = "articles",
  language?: string
) => {
  return fetchWordPressData(
    postType,
    {
      search: searchTerm,
      per_page: 100,
    },
    language
  );
};

// ACF Fields Helper - Get ACF fields with language support
export const getACFFields = (post: any, language?: string) => {
  if (!post.acf) return {};

  // WPML automatically handles ACF field translation
  // The ACF fields will be in the current language
  return post.acf;
};

// Get translated post by slug
export const getPostBySlug = async (
  slug: string,
  postType: string = "products",
  language?: string
) => {
  return fetchWordPressData(
    postType,
    {
      slug: slug,
      per_page: 1,
    },
    language
  );
};

// Get related posts (same category, different language)
export const getRelatedPosts = async (
  postId: number,
  categoryId: number,
  postType: string = "articles",
  language?: string
) => {
  return fetchWordPressData(
    postType,
    {
      [`${postType}_categories`]: categoryId,
      exclude: postId,
      per_page: 3,
    },
    language
  );
};

export const getFeaturesData = async (language?: string) => {
  return fetchWordPressData("features_page/874", {}, language);
};

export const getStaticVsDynamicData = async (language?: string) => {
  return fetchWordPressData("static_vs_dynamic/919", {}, language);
};

export const getFAQData = async (language?: string) => {
  return fetchWordPressData("faqs/846", {}, language);
};
